import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Clock3, Inbox, RadioTower, ShieldCheck, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { EmptyState, InfoRow, PageHeader, StatCard, StatusBadge, Surface } from '../../components/ui';
import { DeviceHardware } from '../../components/DeviceLogo';

const ONLINE_MS = 2 * 60 * 1000;

function asUtcDate(value) {
  if (!value) return null;
  return new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
}

function isOnline(lastSeen) {
  const date = asUtcDate(lastSeen);
  return date && Date.now() - date.getTime() < ONLINE_MS;
}

function formatDate(value) {
  const date = asUtcDate(value);
  return date ? date.toLocaleString() : '—';
}

function DeviceStatus({ device, t }) {
  if (device.status !== 'provisioned') return <StatusBadge tone="warning">{t('userDetail.statusPending')}</StatusBadge>;
  const online = isOnline(device.last_seen);
  return <StatusBadge tone={online ? 'success' : 'muted'}>{online ? t('userDetail.statusOnline') : t('userDetail.statusOffline')}</StatusBadge>;
}

export default function UserDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.getUser(id), api.getUserDevices(id)])
      .then(([userData, deviceData]) => {
        setUser(userData);
        setDevices(deviceData);
      })
      .catch((err) => setError(err.message || t('userDetail.notFound')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) {
    return <div className="page loading-page"><div className="spinner-border" role="status" aria-label={t('adminSettings.loading')} /></div>;
  }

  if (error || !user) {
    return (
      <div className="page page-narrow">
        <button type="button" className="btn btn-outline-secondary mb-3" onClick={() => navigate('/admin/users')}><ArrowLeft className="icon" aria-hidden="true" />{t('userDetail.back')}</button>
        <div className="alert alert-danger" role="alert">{error || t('userDetail.notFound')}</div>
      </div>
    );
  }

  const userTone = user.is_banned ? 'danger' : user.is_admin ? 'accent' : 'success';
  const userStatus = user.is_banned
    ? t('userDetail.statusBanned')
    : user.is_admin ? t('userDetail.statusAdmin') : t('userDetail.statusActive');
  const onlineCount = devices.filter((device) => device.status === 'provisioned' && isOnline(device.last_seen)).length;
  const pendingCount = devices.filter((device) => device.status !== 'provisioned').length;

  return (
    <div className="page">
      <button type="button" className="back-link" onClick={() => navigate('/admin/users')}><ArrowLeft className="icon" aria-hidden="true" />{t('userDetail.back')}</button>
      <PageHeader
        icon={UserRound}
        title={user.username}
        subtitle={user.email}
        actions={<StatusBadge tone={userTone}>{userStatus}</StatusBadge>}
      />

      <div className="stat-grid">
        <StatCard icon={RadioTower} label={t('userDetail.statsDevices')} value={devices.length} />
        <StatCard icon={ShieldCheck} label={t('userDetail.statsOnline')} value={onlineCount} tone="success" />
        <StatCard icon={Clock3} label={t('userDetail.statsPending')} value={pendingCount} tone="warning" />
      </div>

      <div className="content-grid">
        <Surface className="grid-span-4" title={t('userDetail.profileTitle')} icon={UserRound}>
          <div className="surface-body p-0">
            <InfoRow label={t('userDetail.fieldId')}>#{user.id}</InfoRow>
            <InfoRow label={t('userDetail.fieldUsername')}><strong>{user.username}</strong></InfoRow>
            <InfoRow label={t('userDetail.fieldEmail')}>{user.email}</InfoRow>
            <InfoRow label={t('userDetail.fieldRole')}>{user.is_admin ? t('userDetail.roleAdmin') : t('userDetail.roleUser')}</InfoRow>
            <InfoRow label={t('userDetail.fieldStatus')}><span className={`tone-text-${userTone}`}>{userStatus}</span></InfoRow>
            <InfoRow label={t('userDetail.fieldRegistered')}>{formatDate(user.created_at)}</InfoRow>
          </div>
        </Surface>

        <Surface className="grid-span-8" title={t('userDetail.devicesTitle', { count: devices.length })} icon={RadioTower}>
          {devices.length === 0 ? <EmptyState icon={Inbox} title={t('userDetail.noDevices')} /> : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead><tr><th>{t('userDetail.colSerial')}</th><th>{t('userDetail.colStatus')}</th><th>{t('userDetail.colLastActive')}</th><th>{t('userDetail.colTransport')}</th><th aria-label={t('adminDevices.edit')} /></tr></thead>
                <tbody>{devices.map((device) => (
                  <tr
                    key={device.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/admin/devices/${device.id}`)}
                    onKeyDown={(event) => event.key === 'Enter' && navigate(`/admin/devices/${device.id}`)}
                  >
                    <td><DeviceHardware device={device} /><code className="d-block small mt-1">{device.serial_number}</code></td>
                    <td><DeviceStatus device={device} t={t} /></td>
                    <td className="small text-muted text-nowrap">{formatDate(device.last_seen)}</td>
                    <td>{device.transport ? <code>{device.transport}</code> : <span className="text-muted">—</span>}</td>
                    <td><ChevronRight className="icon row-arrow" aria-hidden="true" /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Surface>
      </div>
    </div>
  );
}
